import { Injectable, signal, effect, computed } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class QrStateService {
  private _tableId = signal<string | null>(null);
  private _tableNumber = signal<string | null>(null);
  private _eventId = signal<string | null>(null);
  private _companyId = signal<string | null>(null);
  private _waiterToken = signal<string | null>(null);
  private _initialized = signal<boolean>(false);

  readonly tableId = this._tableId.asReadonly();
  readonly tableNumber = this._tableNumber.asReadonly();
  readonly eventId = this._eventId.asReadonly();
  readonly companyId = this._companyId.asReadonly();
  readonly waiterToken = this._waiterToken.asReadonly();
  readonly initialized = this._initialized.asReadonly();
  readonly isWaiterMode = computed(() => !!this._waiterToken());

  constructor(private router: Router) {
    // Persist state to localStorage
    effect(() => {
      const tableId = this._tableId();
      const tableNumber = this._tableNumber();
      const eventId = this._eventId();
      const companyId = this._companyId();
      const waiterToken = this._waiterToken();

      if (tableId) localStorage.setItem('qr_tableId', tableId);
      if (tableNumber) localStorage.setItem('qr_tableNumber', tableNumber);
      if (eventId) localStorage.setItem('qr_eventId', eventId);
      if (companyId) localStorage.setItem('qr_companyId', companyId);
      if (waiterToken) localStorage.setItem('qr_waiterToken', waiterToken);
    });
  }

  initializeFromUrl(route: ActivatedRoute): void {
    if (this._initialized()) return;

    // Handle waiterToken from Fragment/Hash
    route.fragment.subscribe(fragment => {
      if (fragment) {
        const fragParams = new URLSearchParams(fragment);
        const waiterToken = fragParams.get('waiterToken');
        if (waiterToken) {
          this._waiterToken.set(waiterToken);
        }
      }
    });

    route.queryParams.subscribe(params => {
      const tableId = params['tableId'];
      const eventId = params['eventId'];
      const companyId = params['companyId'];

      // tableId is optional: guest QR codes carry one, waiter links do not
      if (eventId) {
        this._eventId.set(eventId);
        if (companyId) this._companyId.set(companyId);

        if (tableId) {
          this._tableId.set(tableId);
          // The number belonging to the new id is resolved later from the API
          this._tableNumber.set(null);
          localStorage.removeItem('qr_tableNumber');
        } else {
          // A fresh link without a table must not inherit a previously selected one
          this.clearTable();
        }

        this._initialized.set(true);

        // Clean URL by removing query parameters AND hash
        this.router.navigate([], {
          queryParams: {},
          fragment: undefined,
          replaceUrl: true
        });
      } else {
        // Try to restore from localStorage if no URL params
        const storedTableId = localStorage.getItem('qr_tableId');
        const storedTableNumber = localStorage.getItem('qr_tableNumber');
        const storedEventId = localStorage.getItem('qr_eventId');
        const storedCompanyId = localStorage.getItem('qr_companyId');
        const storedWaiterToken = localStorage.getItem('qr_waiterToken');

        if (storedEventId) {
          this._eventId.set(storedEventId);
          if (storedTableId) this._tableId.set(storedTableId);
          if (storedTableNumber) this._tableNumber.set(storedTableNumber);
          if (storedCompanyId) this._companyId.set(storedCompanyId);
          if (storedWaiterToken) this._waiterToken.set(storedWaiterToken);
        }

        this._initialized.set(true);
      }
    });
  }

  selectTable(tableId: string, tableNumber: string): void {
    this._tableId.set(tableId);
    this._tableNumber.set(tableNumber);
  }

  setTableNumber(tableNumber: string): void {
    this._tableNumber.set(tableNumber);
  }

  clearTable(): void {
    this._tableId.set(null);
    this._tableNumber.set(null);
    localStorage.removeItem('qr_tableId');
    localStorage.removeItem('qr_tableNumber');
  }

  setEventId(eventId: string): void {
    this._eventId.set(eventId);
  }

  clear(): void {
    this._tableId.set(null);
    this._tableNumber.set(null);
    this._eventId.set(null);
    this._companyId.set(null);
    this._waiterToken.set(null);
    localStorage.removeItem('qr_tableId');
    localStorage.removeItem('qr_tableNumber');
    localStorage.removeItem('qr_eventId');
    localStorage.removeItem('qr_companyId');
    localStorage.removeItem('qr_waiterToken');
  }

  hasQrData(): boolean {
    return !!(this._tableId() && this._eventId());
  }
}
